/**
 * Hook para buscar dados do gráfico evolutivo de agendamento online
 * 
 * Chama a RPC get_online_scheduling_chart com granularidade diária ou mensal.
 * 
 * RELEASE: 2026-02-15
 * VERSION: 1.0.0
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { format } from 'date-fns';
import type { PeriodFilter } from '@/hooks/useCrmOverviewFilters';

const logger = createLogger('OnlineSchedulingChart');

/** Tipagem de um ponto do gráfico */
export interface ChartDataPoint {
  period_label: string;
  confirmed_count: number;
  cancelled_count: number;
}

/** Parâmetros do hook */
interface UseOnlineSchedulingChartParams {
  granularity: 'daily' | 'monthly';
  period: PeriodFilter;
  eventTypeId?: string | null;
}

/**
 * Hook que busca dados agrupados para o gráfico evolutivo
 */
export function useOnlineSchedulingChart({ granularity, period, eventTypeId }: UseOnlineSchedulingChartParams) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Quando mensal, a RPC internamente usa últimos 12 meses
  const startDate = granularity === 'monthly' ? null : format(period.startDate, 'yyyy-MM-dd');
  const endDate = granularity === 'monthly' ? null : format(period.endDate, 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['online-scheduling-chart', tenantId, granularity, startDate, endDate, eventTypeId],
    queryFn: async (): Promise<ChartDataPoint[]> => {
      logger.info('Buscando dados do gráfico', { granularity, startDate, endDate });

      const { data, error } = await supabase.rpc('get_online_scheduling_chart', {
        p_granularity: granularity,
        p_start_date: startDate,
        p_end_date: endDate,
        p_event_type_id: eventTypeId || null,
      });

      if (error) {
        logger.error('Erro ao buscar dados do gráfico', error);
        throw error;
      }

      // Mapeia para números (RPC retorna BIGINT como string)
      return (data ?? []).map((row: any) => ({
        period_label: row.period_label,
        confirmed_count: Number(row.confirmed_count ?? 0),
        cancelled_count: Number(row.cancelled_count ?? 0),
      }));
    },
    enabled: !!tenantId,
  });
}
